import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { FAQEmbed } from '../components/FAQEmbed';
import { Tx } from '../components/EditMode';
import { useI18n } from '../i18n/I18nContext';
import { useCurrency } from '../i18n/useCurrency';
import { useLocalizedContent } from '../i18n/useLocalizedContent';
import { Heart, Calendar, ArrowRight, ExternalLink, HandHeart, Lock, Users, Compass } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Dashboard = () => {
  const [data, setData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [links, setLinks] = useState([]);
  const [teaserCount, setTeaserCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { format, currency, setCurrency, supported } = useCurrency();
  const localized = useLocalizedContent();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [dashRes, postsRes, settingsRes, linksRes, teaserRes] = await Promise.all([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/journey?limit=3`),
        axios.get(`${API}/settings/public`),
        axios.get(`${API}/external-links`),
        axios.get(`${API}/journey/teaser-count`)
      ]);
      setData(dashRes.data);
      setPosts(postsRes.data);
      setSettings(settingsRes.data);
      setLinks(linksRes.data);
      setTeaserCount(teaserRes.data.supporter_only_count || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (s) => new Date(s).toLocaleDateString(lang === 'no' ? 'nb-NO' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  const monthlyProgress = data ? (data.active_support / data.monthly_goal) * 100 : 0;
  const monthlyPledgedProgress = data ? ((data.active_support + data.pledged_support) / data.monthly_goal) * 100 : 0;
  const oneTimeProgress = data ? (data.one_time_gifts / data.one_time_goal) * 100 : 0;

  const profileImageUrl = settings?.profile_image
    ? `${process.env.REACT_APP_BACKEND_URL}${settings.profile_image}`
    : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <NavBar />

      {/* Cinematic Hero — aviation inspired */}
      <div data-testid="mission-hero-section" className="relative w-full h-[85vh] min-h-[560px] max-h-[820px] overflow-hidden">
        {/* Background: video > image > gradient */}
        {settings?.hero_video_url ? (
          <video
            data-testid="hero-bg-video"
            autoPlay muted loop playsInline
            poster={profileImageUrl || undefined}
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={settings.hero_video_url} type="video/mp4" />
          </video>
        ) : profileImageUrl ? (
          <img
            data-testid="mission-profile-image"
            src={profileImageUrl}
            alt={settings?.mission_name}
            className="absolute inset-0 w-full h-full object-cover scale-105"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-sky-900 via-blue-900 to-slate-900"></div>
        )}

        {/* Cinematic gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/55 to-slate-900/90"></div>

        {/* Subtle aviation pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none"
             style={{
               backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.4) 0px, transparent 1px)',
               backgroundSize: '32px 32px',
             }}></div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 sm:px-6 lg:px-8">
          <h1 data-testid="mission-name" className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 font-['Outfit'] tracking-tight drop-shadow-2xl">
            <Tx k="home.hero.welcome" />
          </h1>

          {localized(settings, 'bio') && (
            <p data-testid="mission-bio" className="text-base sm:text-lg text-white/90 max-w-2xl mx-auto font-['Manrope'] leading-relaxed whitespace-pre-wrap drop-shadow mb-10">
              {localized(settings, 'bio')}
            </p>
          )}

          <div className="flex flex-col items-center gap-3">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center">
              <button
                data-testid="hero-cta-btn"
                onClick={() => navigate('/signup')}
                className="group inline-flex items-center gap-2 px-8 py-4 bg-amber-500 text-slate-900 rounded-full font-bold text-base hover:bg-amber-400 transition-all duration-200 shadow-2xl hover:shadow-amber-500/40 hover:scale-105"
              >
                <HandHeart className="w-5 h-5" />
                <Tx k="home.cta.partner_monthly" />
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate('/mission')}
                className="inline-flex items-center gap-2 px-6 py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white rounded-full font-semibold text-sm hover:bg-white/20 transition-all"
              >
                <Compass className="w-4 h-4" />
                <Tx k="home.cta.explore" />
              </button>
            </div>
            <p data-testid="hero-cta-reassurance" className="flex items-center gap-1.5 text-xs text-white/70 mt-1">
              <Lock className="w-3 h-3" />
              <Tx k="home.invite.reassurance" />
            </p>
          </div>

          {/* Stats strip — % monthly goal (incl. pledged) · % one-time · total supporters */}
          <div data-testid="hero-stats" className="mt-12 grid grid-cols-3 gap-6 sm:gap-12 text-white max-w-3xl">
            <div className="text-center">
              <div data-testid="hero-stat-monthly" className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit'] text-amber-300">
                {Math.round(monthlyPledgedProgress)}<span className="text-lg">%</span>
              </div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-white/70 mt-1"><Tx k="home.stats.monthly_pledged" /></div>
            </div>
            <div className="text-center border-x border-white/20">
              <div data-testid="hero-stat-onetime" className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit'] text-amber-300">
                {Math.round(oneTimeProgress)}<span className="text-lg">%</span>
              </div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-white/70 mt-1"><Tx k="home.stats.onetime_progress" /></div>
            </div>
            <div className="text-center">
              <div data-testid="hero-stat-supporters" className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit']">{data?.total_supporters_count || 0}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-white/70 mt-1"><Tx k="home.stats.supporters" /></div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative overflow-hidden py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Currency switcher */}
          <div data-testid="currency-switcher" className="flex justify-end items-center gap-2 mb-4">
            <span className="text-xs text-slate-500">Show amounts in:</span>
            <div className="flex gap-1 bg-white rounded-full border border-slate-200 p-1 shadow-sm">
              {supported.map(c => (
                <button
                  key={c}
                  data-testid={`currency-btn-${c}`}
                  onClick={() => setCurrency(c)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                    currency === c ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Goals Progress */}
          <div className="space-y-6">
            <div data-testid="monthly-goal-card" className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6 gap-4">
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-semibold text-slate-800 mb-1 font-['Outfit']"><Tx k="home.monthly_goal.title" /></h3>
                  <p className="text-sm text-slate-600 font-['Manrope'] mb-2 leading-relaxed"><Tx k="home.monthly_goal.subtitle" /></p>
                  <p className="text-sm text-slate-500 font-['Manrope'] flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span data-testid="supporters-count-inline">
                      {(data?.active_supporters_count || 0)} {t('home.supporters_count.active')} · {(data?.pledged_supporters_count || 0)} {t('home.supporters_count.pledged')}
                    </span>
                  </p>
                </div>
                <div className="text-left sm:text-right flex-shrink-0">
                  <p className="text-2xl font-bold text-slate-900">{format(data?.monthly_goal)}</p>
                  <p className="text-sm text-slate-500"><Tx k="home.monthly_goal.target" /></p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600 font-medium"><Tx k="home.monthly_goal.active_support" /></span>
                    <span className="text-green-600 font-bold">{format(data?.active_support || 0)}</span>
                  </div>
                  <div data-testid="active-support-progress" className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="absolute h-full bg-green-500 rounded-full transition-all duration-500"
                         style={{ width: `${Math.min(monthlyProgress, 100)}%` }}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600 font-medium"><Tx k="home.monthly_goal.pledged_support" /></span>
                    <span className="text-yellow-600 font-bold">{format((data?.active_support || 0) + (data?.pledged_support || 0))}</span>
                  </div>
                  <div data-testid="pledged-support-progress" className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="absolute h-full bg-yellow-500 rounded-full transition-all duration-500"
                         style={{ width: `${Math.min(monthlyPledgedProgress, 100)}%`,
                                  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.3) 5px, rgba(255,255,255,0.3) 10px)' }}></div>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 italic"><Tx k="home.monthly_goal.pledge_note" /></p>
                </div>
              </div>
            </div>

            <div data-testid="onetime-goal-card" className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6 gap-4">
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-semibold text-slate-800 mb-1 font-['Outfit']"><Tx k="home.onetime_goal.title" /></h3>
                  <p className="text-sm text-slate-600 font-['Manrope'] leading-relaxed"><Tx k="home.onetime_goal.subtitle" /></p>
                </div>
                <div className="text-left sm:text-right flex-shrink-0">
                  <p className="text-2xl font-bold text-slate-900">{format(data?.one_time_goal)}</p>
                  <p className="text-sm text-slate-500"><Tx k="home.monthly_goal.target" /></p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600 font-medium"><Tx k="home.onetime_goal.raised" /></span>
                    <span className="text-blue-600 font-bold">{format(data?.one_time_gifts || 0)}</span>
                  </div>
                  <div data-testid="onetime-gifts-progress" className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="absolute h-full bg-blue-600 rounded-full transition-all duration-500"
                         style={{ width: `${Math.min(oneTimeProgress, 100)}%` }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Support Through Wycliffe — language-aware */}
          {((lang === 'no' ? settings?.wycliffe_info_url_no : settings?.wycliffe_info_url) || '') && (
            <div data-testid="wycliffe-section" className="mt-12 bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-blue-100 rounded-lg"><HandHeart className="w-6 h-6 text-blue-600" /></div>
                <h3 className="text-xl sm:text-2xl font-semibold text-slate-800 font-['Outfit']"><Tx k="home.wycliffe.title" /></h3>
              </div>
              <p className="text-slate-700 mb-5 font-['Manrope'] leading-relaxed whitespace-pre-line">
                <Tx k="home.wycliffe.body" />
              </p>
              <a data-testid="wycliffe-link"
                 href={lang === 'no' ? settings?.wycliffe_info_url_no : settings?.wycliffe_info_url}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full font-semibold text-sm hover:bg-blue-700 shadow-sm hover:shadow-md transition-all">
                <Tx k="home.wycliffe.cta" />
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}

          {/* Supporter Teaser */}
          {teaserCount > 0 && (
            <div data-testid="supporter-teaser-card" className="mt-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-md p-8 text-white">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-white/20 rounded-xl flex-shrink-0"><Lock className="w-6 h-6" /></div>
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-bold mb-2 font-['Outfit']">
                    {teaserCount} {teaserCount === 1 ? t('home.teaser.title_one') : t('home.teaser.title_many')}
                  </h3>
                  <p className="text-blue-100 mb-5 font-['Manrope']">
                    {localized(settings, 'teaser_text') || t('home.teaser.default_text')}
                  </p>
                  <button data-testid="teaser-signup-btn" onClick={() => navigate('/signup')}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-full font-medium hover:bg-blue-50 transition-all">
                    <Heart className="w-4 h-4" /> <Tx k="home.cta.become_supporter" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Journey Feed Preview */}
          {posts.length > 0 && (
            <div className="mt-16">
              <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
                <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight"><Tx k="home.journey.title" /></h3>
                <button data-testid="view-all-journey-btn" onClick={() => navigate('/journey')}
                        className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-medium text-sm">
                  <Tx k="home.journey.view_all" /> <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <div data-testid="journey-preview-list" className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {posts.map((post, index) => (
                  <article key={post.id} data-testid={`journey-preview-${index}`}
                           onClick={() => navigate('/journey')}
                           className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden cursor-pointer hover:-translate-y-1 transition-transform duration-200">
                    {post.image_url && (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${post.image_url}`}
                           alt={post.title} className="w-full h-40 object-cover" />
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                        <Calendar className="w-3.5 h-3.5" />
                        <time>{formatDate(post.created_at)}</time>
                      </div>
                      <h4 className="text-lg font-bold text-slate-900 mb-2 font-['Outfit'] line-clamp-2">{localized(post, 'title')}</h4>
                      <p className="text-sm text-slate-600 line-clamp-3 font-['Manrope']">{localized(post, 'content')}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
          {/* FAQ preview */}
          <div className="mt-16">
            <FAQEmbed limit={3} />
          </div>
        </div>
      </div>
    </div>
  );
};
